import {AfterViewInit, Directive, inject, input} from '@angular/core';
import {WjTabPanel} from '@mescius/wijmo.angular2.nav';
import {EventArgs} from '@mescius/wijmo';
import {TabPanel} from '@mescius/wijmo.nav';
import {AppSessionStorage} from '../lib/app-localstorage';

/**
 * A directive that enables the persistence of the selected tab state for a Wijmo TabPanel (`WjTabPanel`)
 * in the browser's `sessionStorage`. The directive listens for changes to the selected tab index and saves
 * the state, and it also restores the previously selected tab index upon initialization, if available.
 *
 * This directive should be applied to elements containing a `WjTabPanel` component.
 * If the directive is not properly attached to a `WjTabPanel` component, a warning will be emitted, and no state
 * persistence will occur.
 */
@Directive({
    selector: '[appWjTabStatePersist]',
})
export class WjTabStatePersistDirective implements AfterViewInit {

    // a key that uniquely identifies the state to be persisted
    // the key is used as part of the sessionStorage key, so it should not contain any special characters.
    public readonly tabStateKey = input.required<string>();

    private host = inject(WjTabPanel, { host: true, optional: false });

    constructor() {
    }

    ngAfterViewInit(): void {
        if (!this.host) {
            console.warn('WjTabStatePersistDirective must be used on a WjTab component.');
        } else {
            this.host.initialized?.subscribe(this.onTabPanelInitialized.bind(this));
            this.host.selectedIndexChanged.addHandler(this.onSelectedIndexChanged.bind(this));
        }
    }

    private onTabPanelInitialized() {
        const parsedIndex = AppSessionStorage.getNumberItem(this.getStorageKey());
        if (!parsedIndex || parsedIndex < 0 || parsedIndex > this.host.tabs.length - 1) {
            console.warn(`wj-tab-state-persist: invalid saved index ${parsedIndex}, ignoring`);
            AppSessionStorage.removeItemByKey(this.getStorageKey());
        } else {
            this.host.selectedIndex = parsedIndex;
        }
    }

    private onSelectedIndexChanged(sender: TabPanel, args: EventArgs): void {
        if (sender.containsFocus()) {
            const selectedIndex = sender.selectedIndex;
            console.log(`wj-tab-state-persist: selectedIndex changed`, selectedIndex);
            AppSessionStorage.setNumberItem(this.getStorageKey(), selectedIndex);
        }
    }

    private getStorageKey(): string {
        return `wj-tab-state-${this.tabStateKey()}`;
    }
}
